// TESSERAX — VOIES de déblocage (constellations d'archétypes de build).
//
// Le catalogue de reliques portait déjà des « decks » implicites (Bascule, Croix, Continuum,
// Pli, Zone) : viables et divers (cf. docs/balance-baseline.md), mais INVISIBLES — le codex
// était un mur plat de ~58 verrous, et rien ne disait au joueur « pratique la Croix pour
// ouvrir son deck ». Une VOIE rend l'archétype lisible : 5 paliers (I→V, couronnés par la
// légendaire du deck), débloqués par la MÉTRIQUE DE PRATIQUE de l'archétype (cf.
// core/meta.MetaStats : croix, tesseras, continuum, zoneLines, bascules).
//
// Ce module porte la STRUCTURE seule (voie → paliers → ids de reliques) : les SEUILS restent
// dans relics.UNLOCK_REQS (la « SOURCE UNIQUE » documentée, consommée par unlocksFor, le codex
// et le nudge d'objectif). Un test (tests/voies.test.ts) verrouille la cohérence des deux
// tables : ids existants, sans doublon inter-voies, seuils croissants par palier sur la
// métrique de la voie, palier final légendaire, socle libre = complément exact.
//
// La RÉSONANCE de deck (cf. core/resonance) compte l'appartenance EXPLICITE à une voie
// (voieOf), pas les tags bruts — sinon les reliques qui portent `zone` incidemment (Prisme,
// Faille…) déclencheraient la résonance du Flux sans faire partie du deck.
//
// Module PUR : ni THREE ni DOM, pas de Math.random.

import { RELICS, STARTER_UNLOCKS, UNLOCK_REQS, relicById } from './relics';
import type { UnlockMetric, UnlockStats } from './relics';

export type VoieId = 'bascule' | 'croix' | 'continuum' | 'pli' | 'flux';

export interface VoieTier { relics: string[] }   // ids ; seuils lus dans UNLOCK_REQS
export interface Voie {
  id: VoieId;
  name: string;            // FR inline (i18n voie.<id>.name)
  glyph: string;           // glyphe console (texte, jamais d'emoji — cf. docs/ui-console.md)
  metric: UnlockMetric;    // métrique de PRATIQUE qui fait monter la voie
  tiers: VoieTier[];       // paliers I→V ; le premier peut être des starters (seuil 0)
}

export const VOIES: Voie[] = [
  { id: 'bascule', name: 'Voie de la Bascule', glyph: '⟲', metric: 'bascules', tiers: [
    { relics: ['gyro', 'rouage', 'tectonique', 'avalanche'] },     // I — starters (la porte d'entrée)
    { relics: ['resonance', 'pendule'] },                          // II — 25 / 40 Bascules
    { relics: ['sablier', 'sismographe'] },                        // III — 50 / 60
    { relics: ['resonateur', 'onde_choc'] },                       // IV — 80 / 100
    { relics: ['miroir'] },                                        // V — 160 (clé de voûte)
  ] },
  { id: 'croix', name: 'Voie de la Croix', glyph: '✛', metric: 'croix', tiers: [
    { relics: ['rosace'] },                                        // I — 2 Croix
    { relics: ['croisement'] },                                    // II — 6
    { relics: ['quadrant'] },                                      // III — 14
    { relics: ['meridien'] },                                      // IV — 30
    { relics: ['mandala'] },                                       // V — 60
  ] },
  { id: 'continuum', name: 'Voie du Continuum', glyph: '⟁', metric: 'continuum', tiers: [
    { relics: ['sillage'] },                                       // I — 3 continuums
    { relics: ['elan'] },                                          // II — 10
    { relics: ['relais'] },                                        // III — 22
    { relics: ['apogee'] },                                        // IV — 45
    { relics: ['ligne_de_monde'] },                                // V — 90
  ] },
  { id: 'pli', name: 'Voie du Pli', glyph: '⧉', metric: 'tesseras', tiers: [
    { relics: ['charniere'] },                                     // I — starter (la porte d'entrée 4D)
    { relics: ['origami'] },                                       // II — 3 TESSERAX
    { relics: ['palimpseste'] },                                   // III — 9
    { relics: ['moebius'] },                                       // IV — 20
    { relics: ['syzygie'] },                                       // V — 40
  ] },
  { id: 'flux', name: 'Voie du Flux', glyph: '◉', metric: 'zoneLines', tiers: [
    { relics: ['aimant', 'catalyseur', 'fondation', 'metronome'] },// I — starters (moteur de Zone/combo)
    { relics: ['faille', 'sourcier'] },                            // II — 10 / 18 lignes banquées
    { relics: ['gisement', 'fractale'] },                          // III — 30 / 45
    { relics: ['harmonique', 'vortex', 'crescendo'] },             // IV — 60 / 80 / 100
    { relics: ['prophetie'] },                                     // V — 160
  ] },
];

// Map inversée id de relique → id de voie (mémoïsée : catalogues figés au chargement).
const _voieOf: Record<string, VoieId> = {};
for (const v of VOIES) for (const t of v.tiers) for (const id of t.relics) _voieOf[id] = v.id;

export function voieOf(relicId: string): VoieId | null { return _voieOf[relicId] || null; }
export function voieById(id: VoieId): Voie | undefined { return VOIES.find(v => v.id === id); }

// Nombre de reliques d'une voie dans un build (l'entrée de la Résonance de deck).
export function deckCount(ownedIds: string[], voie: VoieId): number {
  let n = 0;
  for (const id of ownedIds) if (_voieOf[id] === voie) n++;
  return n;
}

// SOCLE LIBRE — les reliques hors voie (moteurs génériques score/contrôle/risque),
// dérivé par différence → croît automatiquement avec le catalogue (testé).
export const SOCLE_LIBRE: string[] = RELICS.filter(r => !_voieOf[r.id]).map(r => r.id);

// Seuil de déblocage d'une relique SUR LA MÉTRIQUE DE SA VOIE (0 = starter, toujours offerte).
// `null` si la relique n'appartient à aucune voie ou n'a pas de chemin sur cette métrique.
export function voieThreshold(relicId: string): number | null {
  const voie = _voieOf[relicId];
  if (!voie) return null;
  if (STARTER_UNLOCKS.includes(relicId)) return 0;
  const req = UNLOCK_REQS[relicId];
  if (!req) return null;
  const metric = voieById(voie)!.metric;
  if (req.metric === metric) return req.threshold;
  const alt = (req.or || []).find(p => p.metric === metric);
  return alt ? alt.threshold : null;
}

// Progression d'une voie pour des stats données : valeur courante de la métrique, prochain
// palier NON atteint (index 0-based, tiers.length si tout est ouvert) et son seuil.
// Alimente l'UI du codex (barre de métrique par voie) — lecture seule, aucun effet gameplay.
export interface VoieProgress { value: number; nextTier: number; nextThreshold: number | null; }
export function voieProgress(voie: Voie, stats: UnlockStats): VoieProgress {
  const value = (stats[voie.metric] as number) || 0;
  for (let i = 0; i < voie.tiers.length; i++) {
    // Seuil d'un palier = plus haut seuil de voie parmi ses reliques (0 pour un palier starter).
    let tierMax = 0;
    for (const id of voie.tiers[i].relics) { const t = voieThreshold(id); if (t !== null && t > tierMax) tierMax = t; }
    if (value < tierMax) return { value, nextTier: i, nextThreshold: tierMax };
  }
  return { value, nextTier: voie.tiers.length, nextThreshold: null };
}

// Garde-fou de cohérence (utilisé par les tests) : chaque id de voie doit résoudre dans RELICS.
export function voiesCoherent(): boolean {
  for (const v of VOIES) for (const t of v.tiers) for (const id of t.relics) if (!relicById(id)) return false;
  return true;
}
